import _ from 'lodash';
import i18n from 'i18n-js';
import React, { Component } from 'react';
import { Button, Modal, Form, Grid } from 'semantic-grab-ui/src';

import { DEFAULT_LOCALE } from 'config';
import supportedLanguages from 'data/languages.json';

type Props = {
  open?: boolean;
  onCancel?: () => void;
  onConfirm?: (languages: string[]) => void;
  defaultSelectedLangs?: string[];
  // List of languages allowed to toggle in language select modal. If empty, all languages are allowed.
  languagesAllowedToToggle?: string[];
  defaultLangAutoSelect?: boolean;
  disabled?: boolean;
};

type State = {
  selected: string[];
};

type LanguageDetails = {
  label: string;
  flag: string;
  code: string;
};

type RadioChangeData = {
  checked?: boolean;
  value: string;
};

const languageCatalog: Record<string, LanguageDetails> = supportedLanguages;

const getComparableLanguageCode = (language: string = ''): string =>
  language.toLowerCase();

const hasEquivalentLanguageCode = (
  languages: string[] = [],
  language: string,
): boolean => {
  const comparableLanguage = getComparableLanguageCode(language);
  return languages.some(
    (currentLanguage) =>
      getComparableLanguageCode(currentLanguage) === comparableLanguage,
  );
};

const addLanguageCode = (languages: string[], language: string): string[] =>
  (hasEquivalentLanguageCode(languages, language) ? languages : [...languages, language]);

const removeLanguageCode = (languages: string[], language: string): string[] => {
  const comparableLanguage = getComparableLanguageCode(language);
  return languages.filter(
    (currentLanguage) =>
      getComparableLanguageCode(currentLanguage) !== comparableLanguage,
  );
};

export default class LanguageSelectModal extends Component<Props, State> {
  static defaultProps = {
    defaultLangAutoSelect: false,
    defaultSelectedLangs: [],
    disabled: false,
    languagesAllowedToToggle: [],
    onCancel: _.noop,
    onConfirm: _.noop,
    open: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      selected: props.defaultSelectedLangs || [],
    };
  }

  componentDidMount() {
    const {
      defaultLangAutoSelect,
      defaultSelectedLangs = [],
      onConfirm = _.noop,
    } = this.props;

    if (
      !hasEquivalentLanguageCode(defaultSelectedLangs, DEFAULT_LOCALE) &&
      defaultLangAutoSelect
    ) {
      onConfirm([DEFAULT_LOCALE, ...defaultSelectedLangs]);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!this.props.open && nextProps.open) {
      this.setState({
        selected: nextProps.defaultSelectedLangs || [],
      });
    }
  }

  onCancel = () => {
    const { onCancel = _.noop } = this.props;
    onCancel();
  };

  onConfirm = () => {
    const { onConfirm = _.noop } = this.props;
    onConfirm(this.state.selected);
  };

  handleChange = (_event: React.SyntheticEvent, data: RadioChangeData) => {
    this.setState(({ selected }) => ({
      selected: data.checked
        ? addLanguageCode(selected, data.value)
        : removeLanguageCode(selected, data.value),
    }));
  };

  isActive(language: string): boolean {
    return hasEquivalentLanguageCode(this.state.selected, language);
  }

  isToggleEnabled(language: string): boolean {
    const { languagesAllowedToToggle = [] } = this.props;

    return (
      _.isEmpty(languagesAllowedToToggle) ||
      hasEquivalentLanguageCode(languagesAllowedToToggle, language)
    );
  }

  isDefaultLanguage(language: string): boolean {
    const { languagesAllowedToToggle = [] } = this.props;

    return (
      language === DEFAULT_LOCALE &&
      !hasEquivalentLanguageCode(languagesAllowedToToggle, language)
    );
  }

  renderLanguageToggles() {
    const { disabled } = this.props;
    const codes = _.union([DEFAULT_LOCALE], _.keys(languageCatalog)); // force DEFAULT_LOCALE to be the first
    const toggles = _.map(codes, (code) => {
      const language = languageCatalog[code];
      const label = language.label;
      const isDefaultLanguage = this.isDefaultLanguage(code);
      const isActive = this.isActive(code);
      const enableToggle = this.isToggleEnabled(code);

      return (
        <Grid.Column key={code} width={8}>
          <Grid>
            <Grid.Column width={6}>
              <label>{label} {isDefaultLanguage && i18n.t('common.default')}</label>
            </Grid.Column>
            <Grid.Column width={10}>
              <Form.Radio
                toggle
                className="green"
                checked={isActive}
                value={code}
                name={code}
                disabled={isDefaultLanguage || !enableToggle || disabled}
                readOnly={isDefaultLanguage}
                onChange={this.handleChange}
              />
            </Grid.Column>
          </Grid>
        </Grid.Column>
      );
    });

    return (
      <Grid>
        {toggles}
      </Grid>
    );
  }

  render() {
    const { disabled, open } = this.props;
    return (
      <div>
        <Modal size="small" open={open && !disabled}>
          <Modal.Header>
            Select Languages
          </Modal.Header>
          <Modal.Content>
            {this.renderLanguageToggles()}
          </Modal.Content>
          <Modal.Actions>
            <Button negative onClick={this.onCancel}>
              {i18n.t('common.cancel')}
            </Button>
            <Button positive onClick={this.onConfirm}>
              {i18n.t('common.update')}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}
